// src/lib/timelineEvents.ts
//
// PKT-0043a Sidebar Timeline merge v1 · Phase 1
//
// Pure conversion functions that map each existing data source into a unified
// TimelineEvent shape so ClientTimeline can interleave them chronologically.
//
// v1a scope: basic event-type interleave with icon + timestamp + body. No
// expandable raw payload rows · email tracking badges · hover quick actions
// · those land in PKT-0043b (the expandable-rows half of Director #6's split).
//
// Refs:
//   - projects/locatenowpro-client-story-redesign.md §E.4 + §E.6 (data layer)
//   - live/packets/open/PKT-0043-sidebar-timeline-merge-v1.md (parent packet)
//   - live/handoffs/EVENT-0086-checkin-claim-worker-wc-agent-50-pkt-0043a-sidebar-timeline-merge.md

import type {
  ClientActivity,
  ClientNote,
  SavedSearch,
  SentListing,
  SentGuestCard,
} from "../components/clients/types";
import type { Invoice } from "../components/clients/invoiceTypes";

export type TimelineEventKind =
  | "activity" // any row from the activities table
  | "note" // client_notes row
  | "list_sent" // sent_list JSON entry on clients table
  | "search_ran" // saved_searches row
  | "guest_card_sent" // sent_guest_cards row
  | "invoice_sent"; // invoices row (folded in from old Invoices tab)

export interface TimelineEvent {
  // Stable identity within the merged feed (prefixed by kind to avoid id collisions across sources)
  id: string;
  kind: TimelineEventKind;
  // ISO timestamp · used for chronological sort
  ts: string;
  // Sub-classifier (mostly the activities.activity_type value for kind=activity · empty for others)
  subtype?: string;
  // Free-form metadata bag · v1a uses sparingly (kind-specific render in TimelineEvent.tsx)
  metadata?: Record<string, any>;
  // Raw underlying record · v1a only uses for click-through · v1b will expand inline
  raw?: any;
}

const sortDesc = (a: TimelineEvent, b: TimelineEvent): number => {
  const ta = new Date(a.ts).getTime();
  const tb = new Date(b.ts).getTime();
  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;
  return tb - ta;
};

export const activityToTimelineEvent = (
  a: ClientActivity
): TimelineEvent => ({
  id: `activity:${a.id}`,
  kind: "activity",
  ts: a.created_at,
  subtype: a.activity_type,
  metadata: a.metadata || undefined,
  raw: a,
});

export const noteToTimelineEvent = (n: ClientNote): TimelineEvent => ({
  id: `note:${n.id}`,
  kind: "note",
  ts: n.created_at,
  metadata: { content: n.content },
  raw: n,
});

export const sentListToTimelineEvent = (
  s: SentListing
): TimelineEvent => ({
  id: `list:${s.id}`,
  kind: "list_sent",
  ts: s.sent_at,
  metadata: {
    recipient_email: s.recipient_email,
    subject: s.subject,
    property_count: s.property_count,
    unit_count: s.unit_count,
    tracking_id: s.tracking_id,
  },
  raw: s,
});

export const savedSearchToTimelineEvent = (
  s: SavedSearch
): TimelineEvent => ({
  id: `search:${s.id}`,
  kind: "search_ran",
  ts: s.created_at,
  metadata: {
    name: s.name,
    result_count: s.result_count,
  },
  raw: s,
});

export const guestCardToTimelineEvent = (
  g: SentGuestCard
): TimelineEvent => ({
  id: `guestcard:${g.id}`,
  kind: "guest_card_sent",
  ts: g.sent_at,
  metadata: {
    recipient_name: g.recipient_name,
    recipient_email: g.recipient_email,
    property_name: g.property_snapshot?.name,
    neighborhood: g.property_snapshot?.neighborhood,
    tracking_id: g.tracking_id,
    // Without this the timeline calls a hand-submitted registration a send.
    delivery_method: g.delivery_method,
  },
  raw: g,
});

export const invoiceToTimelineEvent = (i: Invoice): TimelineEvent => ({
  id: `invoice:${i.id}`,
  kind: "invoice_sent",
  // Invoice has both created_at and sent_at; prefer sent_at when present
  ts: (i as any).sent_at || i.created_at,
  metadata: {
    invoice_number: (i as any).invoice_number,
    amount: (i as any).amount_total ?? (i as any).amount,
    status: (i as any).status,
    property_name: (i as any).property_name,
  },
  raw: i,
});

/**
 * Build the unified, chronologically-sorted Timeline from all loaded sources.
 *
 * v1a callers pass the already-loaded arrays from DetailDrawer.tsx; this
 * function only merges + sorts. No I/O. Pure.
 */
// Activity types that have a parallel "primary record" in another table —
// these would render as duplicate rows in the Timeline if we included them.
// E.g., `note_added` is logged in activities AND in client_notes; we render
// only the client_notes row (which has the actual content). Same for the
// other entity-bound events: sent_listings / sent_guest_cards / invoices
// each get a dedicated render path below. Keeping the activity rows in the
// DB for analytics (Today widget aggregates · last_contacted_at trigger).
const DUPLICATE_ACTIVITY_TYPES = new Set<string>([
  "note_added",
  "listing_shared",
]);

export const buildTimeline = (sources: {
  activities: ClientActivity[];
  notes: ClientNote[];
  sentListings: SentListing[];
  savedSearches: SavedSearch[];
  sentGuestCards: SentGuestCard[];
  invoices: Invoice[];
}): TimelineEvent[] => {
  const events: TimelineEvent[] = [];
  for (const a of sources.activities) {
    if (DUPLICATE_ACTIVITY_TYPES.has(a.activity_type)) continue;
    events.push(activityToTimelineEvent(a));
  }
  for (const n of sources.notes) events.push(noteToTimelineEvent(n));
  for (const s of sources.sentListings) events.push(sentListToTimelineEvent(s));
  for (const s of sources.savedSearches)
    events.push(savedSearchToTimelineEvent(s));
  for (const g of sources.sentGuestCards)
    events.push(guestCardToTimelineEvent(g));
  for (const i of sources.invoices) events.push(invoiceToTimelineEvent(i));
  events.sort(sortDesc);
  return events;
};
